import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';

import { db } from '../../config/firebase.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { CreateTable } from './table/table-search';
import Spinner from '../../utils/Spinner';
import logger from '../../utils/logger';
import GRADES from '../GradePage/grades.json';
import toPascalCase from '../../utils/toPascalCase';

// Build all grade+subject combos for direct collection queries
const gradeSubjectPairs = GRADES
  .filter(g => g.name !== 'Teachers')
  .flatMap(g => {
    const cleanGrade = g.name.trim().replace(/\s+/g, '');
    return g.subjects.map(s => ({
      grade: cleanGrade,
      subject: toPascalCase(s),
      displaySubject: s,
    }));
  });

const SearchResultsPage = () => {
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryTerm = searchParams.get('q');
    setSearchTerm(queryTerm || '');

    if (queryTerm) {
      fetchResults(queryTerm);
    }
  }, [location]);

  const fetchResults = async (term) => {
    setLoading(true);
    try {
      const allResults = [];

      // Query each grade+subject collection directly: PDFS/{Grade}/{Subject}
      const fetchPromises = gradeSubjectPairs.map(async ({ grade, subject, displaySubject }) => {
        try {
          const colRef = collection(db, 'PDFS', grade, subject);
          const q = query(colRef, where('verified', '==', true));
          const querySnapshot = await getDocs(q);

          return querySnapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
            _ref: docSnap.ref,
            subjectTitle: displaySubject,
          }));
        } catch {
          return []; // Skip collections that don't exist
        }
      });

      const resultsArrays = await Promise.all(fetchPromises);
      resultsArrays.forEach(docs => allResults.push(...docs));

      // Deduplicate by doc id (same doc may appear in multiple grade paths)
      const seen = new Set();
      const dedupedResults = allResults.filter(doc => {
        if (seen.has(doc.id)) return false;
        seen.add(doc.id);
        return true;
      });

      const termLower = term.toLowerCase();
      const filteredResults = dedupedResults.filter(doc => {
        const titleMatch = doc.title && doc.title.toLowerCase().includes(termLower);
        const tagsMatch = doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(termLower));
        const subjectMatch = doc.subjectTitle && doc.subjectTitle.toLowerCase().includes(termLower);
        return titleMatch || tagsMatch || subjectMatch;
      });

      setResults(filteredResults);
    } catch (err) {
      logger.error("Error fetching search results:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    const searchParams = new URLSearchParams();
    searchParams.set('q', searchTerm);
    navigate(`/search?${searchParams.toString()}`);
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 px-5 py-8">
      <GoogleAnalytics page="SearchResultsPage" eventType="searchResults_page" />

      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-block px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">Search</span>
          <h1 className="text-2xl font-bold text-gray-900">Search Results</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">Find verified resources by title, tags, or subject across grades and subjects.</p>
      </div>

      {/* Search bar */}
      <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by keywords..."
            className="flex-1 min-w-[200px] p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Search
          </button>
        </form>
      </div>

      {/* Results table */}
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          {loading ? (
            <Spinner size="lg" text="Searching across all subjects and grades…" />
          ) : results.length > 0 ? (
            <>
              <div className="mb-4 flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </span>
                <span className="text-gray-500 text-sm">for "{searchTerm}"</span>
              </div>
              <CreateTable resources={results} />
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-4xl mb-3">🔍</p>
              <p className="text-gray-600 font-medium">No results found for "{searchTerm}"</p>
              <p className="text-gray-400 text-sm mt-1">Try different keywords, check spelling, or search by subject name.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
